import { useEffect, useMemo, useRef, useState } from "react";
import { money, thumb, SaveChip, Collage, AddToCartButton, TypeChip } from "./shared";

/* Matches a bundle item against a {groupName: value} selection: every
   pair in the item's variant_options must agree with the selection. */
function itemMatches(item, selection) {
  return item.variant_options.every((option) => selection[option.name] === option.value);
}

function firstItemFor(bundle, groupName, value) {
  return bundle.items.find((item) =>
    item.variant_options.some((option) => option.name === groupName && option.value === value)
  );
}

function cheapest(variants) {
  return variants.reduce((min, v) => (v.price < min.price ? v : min), variants[0]);
}

/* An image swatch strip — circular product images, label under the
   selected one. */
function SwatchGroup({ group, bundle, selected, onSelect }) {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-sm font-semibold">{group.name}</span>
        <span className="max-w-[55%] truncate text-sm text-ink-soft">{selected}</span>
      </div>
      {/* padding keeps the selection ring + hover lift inside the scroll
          area instead of getting clipped */}
      <div className="no-scrollbar -mx-1 flex gap-2.5 overflow-x-auto px-1.5 py-1.5">
        {group.values.map((value) => {
          const item = firstItemFor(bundle, group.name, value);
          const image = item?.image_url;
          return (
            <button
              key={value}
              type="button"
              title={value}
              aria-pressed={selected === value}
              onClick={() => onSelect(value)}
              className="swatch-ring h-16 w-16 shrink-0 overflow-hidden rounded-full border-2 border-ink/10 bg-white"
            >
              {image ? (
                <img src={thumb(image, 160)} alt={value} loading="lazy" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center bg-sky-wash text-xs font-bold">
                  {value.slice(0, 2)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* A rich dropdown: image + title + price rows in a custom popover. */
function RichDropdown({ group, bundle, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selectedItem = firstItemFor(bundle, group.name, selected);

  useEffect(() => {
    if (!open) return;
    const close = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <span className="mb-2 block text-sm font-semibold">{group.name}</span>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 rounded-xl border border-ink/15 bg-white px-3.5 py-2.5 text-left transition hover:border-ink/40"
      >
        {selectedItem?.image_url && (
          <img src={thumb(selectedItem.image_url, 120)} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{selected}</span>
          {selectedItem && <span className="block text-xs text-ink-soft">{money(selectedItem.price)}</span>}
        </span>
        <svg className={`h-4 w-4 text-ink/50 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="none">
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul className="absolute z-20 mt-2 max-h-72 w-full overflow-auto rounded-xl border border-ink/10 bg-white py-1.5 shadow-float animate-pop" role="listbox">
          {group.values.map((value) => {
            const item = firstItemFor(bundle, group.name, value);
            return (
              <li key={value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected === value}
                  onClick={() => { onSelect(value); setOpen(false); }}
                  className={`flex w-full items-center gap-3 px-3.5 py-2.5 text-left transition hover:bg-sky-wash ${selected === value ? "bg-sky-wash/60" : ""}`}
                >
                  {item?.image_url && (
                    <img src={thumb(item.image_url, 120)} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{value}</span>
                  {item && <span className="text-xs text-ink-soft">{money(item.price)}</span>}
                  {selected === value && <span className="text-sm">✓</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* Compact custom dropdown for variant-level choices (size etc.) inside a
   slot — text + price rows, no images. */
function MiniSelect({ label, options, selectedId, onSelect }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selected = options.find((o) => o.id === selectedId) || options[0];

  useEffect(() => {
    if (!open) return;
    const close = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex max-w-full items-center gap-2 rounded-lg border border-ink/15 bg-white px-3 py-1.5 text-sm transition hover:border-ink/40"
      >
        <span className="shrink-0 whitespace-nowrap text-ink-soft">{label}:</span>
        <span className="min-w-0 truncate font-medium">{selected?.title || "Default"}</span>
        <span className="shrink-0 text-xs text-ink-soft">{money(selected?.price)}</span>
        <svg className={`h-3.5 w-3.5 shrink-0 text-ink/50 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="none">
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul className="absolute z-20 mt-1.5 max-h-56 w-max min-w-full max-w-[min(20rem,calc(100vw-3rem))] overflow-auto rounded-xl border border-ink/10 bg-white py-1 shadow-float animate-pop" role="listbox">
          {options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                role="option"
                aria-selected={option.id === selected?.id}
                onClick={() => { onSelect(option.id); setOpen(false); }}
                className={`flex w-full items-center gap-4 px-3 py-1.5 text-left text-sm transition hover:bg-sky-wash ${option.id === selected?.id ? "bg-sky-wash/60 font-medium" : ""}`}
              >
                <span className="min-w-0 flex-1 truncate">{option.title || "Default"}</span>
                <span className="shrink-0 text-xs text-ink-soft">{money(option.price)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function InfiniteOptionsWidget({ bundle }) {
  const [selection, setSelection] = useState(() =>
    Object.fromEntries(bundle.option_groups.map((group) => [group.name, group.values[0]]))
  );
  // per-group variant choice (one dimension: size etc.): { [groupName]: variantId }
  const [variantChoice, setVariantChoice] = useState({});

  const isSet = bundle.metadata?.kind === "set";

  const selectProduct = (groupName, value) => {
    setSelection((current) => ({ ...current, [groupName]: value }));
    setVariantChoice((current) => ({ ...current, [groupName]: null })); // new product, fresh variant
  };

  // Resolve each slot: chosen product -> chosen variant.
  const slots = useMemo(() => {
    if (!isSet) return {};
    const resolved = {};
    bundle.option_groups.forEach((group) => {
      const item = firstItemFor(bundle, group.name, selection[group.name]);
      if (!item) return;
      const variants = item.variants?.length
        ? item.variants
        : [{ id: item.variant_id, title: item.variant_title, price: item.price }];
      const variant = variants.find((v) => v.id === variantChoice[group.name]) || cheapest(variants);
      resolved[group.name] = { item, variants, variant };
    });
    return resolved;
  }, [bundle, selection, variantChoice, isSet]);

  const { collageUrls, total } = useMemo(() => {
    if (isSet) {
      const active = bundle.option_groups.map((group) => slots[group.name]).filter(Boolean);
      return {
        collageUrls: active.map((slot) => slot.variant?.image_url || slot.item.image_url),
        total: active.reduce((sum, slot) => sum + (slot.variant?.price ?? slot.item.price), 0),
      };
    }
    const match = bundle.items.find((item) => itemMatches(item, selection)) || bundle.items[0];
    return {
      collageUrls: match ? [match.image_url] : [],
      total: match ? match.price : bundle.price,
    };
  }, [bundle, selection, slots, isSet]);

  const discounted = total * (1 - (bundle.discount_percentage || 0) / 100);

  return (
    // no overflow-hidden: the rich-dropdown popover must be able to
    // extend past the card's bottom edge
    <article className="card">
      <div className="grid grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="rounded-t-[1.2rem] bg-sky-wash/40 p-6 lg:rounded-l-[1.2rem] lg:rounded-tr-none lg:p-8">
          <Collage urls={collageUrls.length ? collageUrls : bundle.items.map((i) => i.image_url)} size="h-80" />
          <p className="mt-3 text-center text-xs text-ink-soft">Preview updates as choices change</p>
        </div>

        <div className="p-6 lg:p-8">
          <TypeChip tone="bg-mint text-ink">Infinite Options</TypeChip>
          <h3 className="display mt-3 text-3xl">{bundle.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink-soft">{bundle.description}</p>

          <div className="mt-6 space-y-5">
            {bundle.option_groups.map((group) => {
              const slot = slots[group.name];
              const showVariants = slot && slot.variants.length > 1;
              const joinedNames = slot?.item.option_names?.join(" / ") || "Size";
              // long option-name chains ("Material / Stone / Size") wrap
              // and blow out the pill — collapse them
              const variantLabel = joinedNames.length <= 18 ? joinedNames : "Options";
              return (
                <div key={group.name}>
                  {group.display === "swatch" ? (
                    <SwatchGroup
                      group={group}
                      bundle={bundle}
                      selected={selection[group.name]}
                      onSelect={(value) => selectProduct(group.name, value)}
                    />
                  ) : (
                    <RichDropdown
                      group={group}
                      bundle={bundle}
                      selected={selection[group.name]}
                      onSelect={(value) => selectProduct(group.name, value)}
                    />
                  )}

                  {showVariants && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <MiniSelect
                        label={variantLabel}
                        options={slot.variants}
                        selectedId={slot.variant.id}
                        onSelect={(variantId) =>
                          setVariantChoice((current) => ({ ...current, [group.name]: variantId }))
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-7 flex items-baseline gap-2.5">
            <span className="font-display text-2xl font-bold">{money(discounted)}</span>
            {bundle.discount_percentage > 0 && (
              <>
                <span className="text-sm text-ink/40 line-through">{money(total)}</span>
                <SaveChip percent={bundle.discount_percentage} />
              </>
            )}
          </div>

          <div className="mt-4">
            <AddToCartButton label="Add my set to cart" />
          </div>
        </div>
      </div>
    </article>
  );
}
